/**
 * Pure logic for the task-inbox bulk-action flow.
 *
 * Extracted from the `useTaskBulkActions` hook so the decisions can
 * be unit-tested without React rendering or query-client wiring.
 * Matches the codebase convention from
 * `task-selection-state.ts` and `persistent-view-filters-decisions.ts`.
 */

import type { BulkCompleteResult } from '@vagusx/clients';

export type BulkCompleteOutcome =
  | { kind: 'all-success'; count: number }
  | {
      kind: 'partial';
      completedCount: number;
      failedCount: number;
      failed: BulkCompleteResult['failed'];
    }
  | { kind: 'all-failure'; failed: BulkCompleteResult['failed'] };

/**
 * Classify a `bulk-complete` API response into one of three UX cases.
 * The component then renders different toasts per case (success +
 * Show / warning / error). Pure function — no toast side effects
 * here so the branching is independently testable.
 */
export function classifyBulkResult(result: BulkCompleteResult): BulkCompleteOutcome {
  if (result.failed.length === 0) {
    return { kind: 'all-success', count: result.completed.length };
  }
  if (result.completed.length === 0) {
    return { kind: 'all-failure', failed: result.failed };
  }
  return {
    kind: 'partial',
    completedCount: result.completed.length,
    failedCount: result.failed.length,
    failed: result.failed,
  };
}

/**
 * Decide what the tri-state header checkbox should do when clicked.
 * Matches Gmail / Linear behavior: when all on the page are
 * selected, click clears the entire selection; otherwise it
 * expands to "select all on page". The indeterminate (some-but-not-
 * all) case still expands — the user is signalling "I want more,"
 * not "I want fewer."
 */
export function decideSelectAllAction(allOnPageSelected: boolean): 'clear' | 'select-all-on-page' {
  return allOnPageSelected ? 'clear' : 'select-all-on-page';
}

/**
 * Detect whether a current selection has gone stale relative to the
 * tasks visible on the page. Used to auto-clear selection when the
 * user changes filters / paginates — without this, the action bar
 * would show "5 selected" pointing at ids no longer rendered, and
 * the bulk action would silently apply to invisible rows.
 *
 * Returns true when at least one selected id is NOT in the visible
 * id set. Caller clears the selection on true.
 *
 * Note: this is more aggressive than checking pure equality. Even
 * if SOME selected ids are still visible, having ANY stale id means
 * the count is wrong and the action's blast radius isn't what the
 * user can see. Cleanest fix is: clear all rather than partial-prune.
 */
export function hasStaleSelection(selectedIds: Iterable<string>, visibleIds: Set<string>): boolean {
  for (const id of selectedIds) {
    if (!visibleIds.has(id)) return true;
  }
  return false;
}
